import { LicenceId } from '@ps/platform-message-framework/common/LicenceId';
import { Cookie as CookieNames, FeatureFlag } from '@ps/web-core-enums';
import * as Sentry from '@sentry/react';
import { isUndefined } from 'lodash';
import { firstValueFrom } from 'rxjs';
import Cookies from 'universal-cookie';

import { connect } from '@swf/core/api/connect';
import type { EffectParams } from '@swf/core/store';
import { select } from '@swf/core/store';

import { idpLogout } from './idpLogin';
import { unsubscribe } from './utils';

const cookies = new Cookies();

const AUTH_COOKIES = [
  CookieNames.BRAND,
  CookieNames.COUNTRY,
  CookieNames.LICENSE,
  CookieNames.LIMITED_MODE,
  CookieNames.SIGNATURE,
  CookieNames.SITE,
  CookieNames.TOKEN,
  CookieNames.WEB_ID
] satisfies readonly CookieNames[];

/**
 * Terminates the session and clears state
 * @param {boolean} payload.implicit - Don't take any post-logout actions, just clear state
 */
const logout = async (
  payload: { implicit?: boolean } = {},
  { rootState, dispatch, request }: EffectParams
) => {
  const domain = select.location.getCookieDomain(rootState);
  const isAuthenticated = select.session.isAuthenticated(rootState);
  const isEmbeddedModal = select.location.isEmbeddedModal(rootState);
  const isEmbeddedRoute = select.location.isEmbeddedRoute(rootState);
  const isEmbedded = select.device.isEmbedded(rootState);
  const license = select.account.getLicense(rootState);
  const isIdpSecondaryLoginEnabled = select.feature.check(
    rootState,
    FeatureFlag.IdpSecondaryLoginEnabled
  );

  dispatch.localSettings.set({ sessionSummary: null, remainingInactivitySeconds: undefined });

  if (!payload.implicit && isEmbeddedRoute) window.close();
  if (!isEmbeddedModal) dispatch.modal.clear();
  if (isIdpSecondaryLoginEnabled) idpLogout();
  if (window.Spade) {
    try {
      window.Spade.api?.logout();
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  unsubscribe();

  try {
    if (payload.implicit || !isAuthenticated) return; // falls through to finally block
    if (isEmbedded) window.starsweb?.auth.logout(); // avoid calling embedded api provided by useEmbeddedAPI

    const { body } = await request({
      service: '/api/v1-preview/auth/session',
      method: 'DELETE',
      http: true
    });

    if (select.account.requireDocumentValidation(rootState)) {
      dispatch.modal.enqueue({ path: '/notifications/documents-verification' });
    }

    switch (license) {
      case LicenceId.ITALY:
        dispatch.modal.enqueue({
          path: '/notifications/logout-summary',
          locationState: { data: body.playingInfo }
        });
        break;
      case LicenceId.USNJ:
      case LicenceId.USPA:
      case LicenceId.USMI:
      case LicenceId.USCO:
        dispatch.modal.enqueue({ path: '/notifications/logout-summary' });
        break;
      case LicenceId.SPAIN:
      case LicenceId.PORTUGAL:
        dispatch.modal.enqueue({
          path: '/notifications/account-information',
          locationState: body.playingInfo
        });
        break;
      case LicenceId.SE:
        dispatch.modal.enqueue({
          path: '/notifications/login-information',
          locationState: { data: body.sessionWinLossData, license }
        });
        break;
      case LicenceId.GR: {
        if (select.account.tempAccount(rootState)) {
          dispatch.modal.enqueue({ path: '/notifications/validate-account-reminder' });
        }
        break;
      }
      case LicenceId.CZ:
      case LicenceId.ROMANIA: {
        const { validateAccountDays } = select.localSettings.get(rootState);
        if (!isUndefined(validateAccountDays)) {
          dispatch.localSettings.set({ validateAccountDays: null });
          dispatch.modal.enqueue({
            path: '/notifications/validate-account-reminder',
            locationState: { param: validateAccountDays }
          });
        }
        break;
      }
      case LicenceId.BELGIUM: {
        if (select.account.isBeAccountValidationRequired(rootState)) {
          dispatch.modal.enqueue({ path: '/notifications/validate-account-reminder' });
        }
        break;
      }
      case LicenceId.BG: {
        if (select.account.isBGAccountValidationRequired(rootState)) {
          dispatch.modal.enqueue({ path: '/notifications/validate-account-reminder' });
        }
        break;
      }
    }
  } catch {
    // do nothing
  } finally {
    AUTH_COOKIES.forEach((name) => cookies.remove(name, { domain, path: '/' }));
    await dispatch.session.clear();
    (await firstValueFrom(connect())).client?.ws.close();
    await dispatch.feature.check();
  }
};

export default logout;
